<template>
  <div class="control-group">
    <label :for="inputId">
      <img :src="icon" :alt="label + ' icon'" class="icon" />
      {{ label }}
    </label>
    <div class="slider-wrapper">
      <input
        :id="sliderId"
        type="range"
        :min="SLIDER_CONFIG.MIN_PERCENT"
        :max="SLIDER_CONFIG.MAX_PERCENT"
        :step="SLIDER_CONFIG.STEP"
        :value="sliderValue"
        class="slider"
        :style="sliderStyles"
        @input="onSliderInput"
      />
      <input
        :id="inputId"
        :type="isCustomValue ? 'text' : 'number'"
        :min="minValue"
        :max="maxValue"
        :value="displayValue"
        class="number-input"
        @input="onInputChange"
        :readonly="isCustomValue"
      />
    </div>
  </div>
</template>

<script>
import { SLIDER_CONFIG } from "@/constants/slider"
import { getRealValue, getSliderPercent, SCALES } from "@utils/scales"
import { getDisplayValue, isCustomValue } from "@utils/sliderDisplay"

export default {
  name: "ControlSlider",

  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
    scaleName: {
      type: String,
      required: true,
      validator: (value) => Object.keys(SCALES).includes(value),
    },
    // Optional unique identifier for multiple sliders
    uniqueId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      SLIDER_CONFIG,
    }
  },

  computed: {
    scale() {
      return SCALES[this.scaleName]
    },

    minValue() {
      return this.scale[0][1]
    },

    maxValue() {
      return this.scale[this.scale.length - 1][1]
    },

    sliderValue() {
      return getSliderPercent(this.value, this.scaleName)
    },

    sliderStyles() {
      const percentage = this.sliderValue
      // Normalize to 0-100% range for visual display (since slider goes to 105)
      const visualPercentage =
        (percentage / SLIDER_CONFIG.MAX_PERCENT) *
        SLIDER_CONFIG.VISUAL_MAX_PERCENT
      return {
        "--progress-percentage": `${visualPercentage}%`,
      }
    },

    sliderId() {
      return this.uniqueId
        ? `${this.scaleName}-slider-${this.uniqueId}`
        : `${this.scaleName}-slider`
    },

    inputId() {
      return this.uniqueId
        ? `${this.scaleName}-value-${this.uniqueId}`
        : `${this.scaleName}-value`
    },

    isCustomValue() {
      return isCustomValue(this.value)
    },

    displayValue() {
      return getDisplayValue(this.value)
    },
  },

  methods: {
    onSliderInput(event) {
      const percent = parseFloat(event.target.value)
      const realValue = getRealValue(percent, this.scaleName)
      this.$emit("input", realValue)
    },

    onInputChange(event) {
      let realValue = parseInt(event.target.value) || 0

      // Prevent exceeding min/max if typing manually
      if (realValue < this.minValue) realValue = this.minValue
      if (realValue > this.maxValue) realValue = this.maxValue

      this.$emit("input", realValue)
    },
  },
}
</script>

<style scoped>
.control-group label {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  color: var(--secondary-text-color);
  margin-bottom: 15px;
}

.icon {
  height: 20px;
  margin-right: 10px;
}

.slider-wrapper {
  display: flex;
  align-items: flex-end;
  max-width: 400px;
  min-width: fit-content;
  gap: 20px;
}

.slider {
  flex: 1;
  accent-color: var(--tertiary-color);
  cursor: pointer;

  /* Hide default appearance for Chrome/Safari */
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 10px;
  background: var(--border-ui);
  border-radius: 5px;
  outline: none;
  margin: 0;
  cursor: pointer;
}

/* THUMB STYLES FOR SLIDERS */
/* Chrome/Safari/Edge */
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #fff;
  border: 2px solid var(--tertiary-color);
  border-radius: 50%;
  margin-top: -5px; /* Center the thumb */
  cursor: grab;
}

/* Firefox */
.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #fff;
  border: 2px solid var(--tertiary-color);
  border-radius: 50%;
  cursor: grab;
}

/* TRACK STYLES FOR SLIDERS */
/* Full part (at left) - Chrome/Safari/Edge */
.slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 10px;
  background: linear-gradient(
    to right,
    var(--tertiary-color) var(--progress-percentage, 0%),
    var(--border-ui) var(--progress-percentage, 0%)
  );
  border-radius: 5px;
  box-sizing: border-box;
}

/* Full part (at left) - Firefox */
.slider::-moz-range-track {
  width: 100%;
  height: 10px;
  background: linear-gradient(
    to right,
    var(--tertiary-color) var(--progress-percentage, 0%),
    var(--border-ui) var(--progress-percentage, 0%)
  );
  border-radius: 5px;
  box-sizing: border-box;
}

/* Active part (full) */
.slider::-moz-range-progress {
  background: var(--tertiary-color);
  height: 10px;
  border-radius: 5px;
}

.number-input {
  width: 30px;
  height: 30px;
  border: 1px solid var(--border-ui);
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  color: var(--tertiary-color);
  box-sizing: border-box;

  /* Remove arrows in number input */
  /* Firefox */
  -moz-appearance: textfield;
  appearance: none;
}

/* Remove arrows in number input */
/* Chrome, Safari, Edge, Opera */
.number-input::-webkit-outer-spin-button,
.number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.number-input {
  -moz-appearance: textfield;
}
</style>
